import React, {Fragment} from 'react'
import {useHistory, useParams} from 'react-router-dom'
import useEffect from 'admin/hooks/useEffect'
import useCredit from 'admin/hooks/useCredit'
import useDocument from 'admin/hooks/useDocument'
import useBoolState from 'admin/hooks/useBoolState'
import useStateObject from 'admin/hooks/useStateObject'
import InputField from 'admin/components/shared/InputField'
import DateField from 'admin/components/shared/DateField'
import Modal from 'admin/components/shared/Modal'
import Header from './shared/Header'
import ShowField from './shared/ShowField/index'
import {toParams} from '../lib/format'
import Deletion from './Deletion'
import useModal from '../hooks/useModal'
import ModalLink from './shared/ModalLink'

export default () => {
  const history = useHistory()
  const {organisationID} = useParams()
  const {creditId, accountId, popModal} = useModal()
  const {credit, errors, update, destroy} = useCredit(organisationID, accountId, creditId)
  const [showConfirmDelete, setConfirmDelete, toggleConfirmDelete] = useBoolState()
  const [form, setForm, resetForm] = useStateObject(credit)

  const title = `Credit ${creditId}`
  useDocument({title})

  useEffect(() => resetForm(credit), [credit], false)

  const handleUpdate = e => {
    e.preventDefault()
    update(form)
  }

  const handleChange = changes => {
    setForm(changes)
  }

  const handleClose = e => {
    e.preventDefault()
    resetForm(credit)
    popModal('credit', ['creditId', 'accountId'])
  }

  if (!credit.id) return null

  const locked = credit.state === 'locked'
  const imported = !!credit.xeroLink

  return (
    <Fragment>
      <Modal onClose={handleClose} flashId='edit-account-credit'>
        {locked && (
          <section className='py-3 section has-background-dark'>
            <div className='container'>
              <span className='icon-text'>
                <span className='icon'>
                  <i className='fas fa-lock'></i>
                </span>
                <span className='is-size-7 has-text-light ml-2'>This credit is locked and cannot be edited.</span>
              </span>
            </div>
          </section>
        )}
        <Header
          title={title}
          subtitle={
            credit.xeroLink && (
              <h5 className='subtitle is-7'>
                <a href={credit.xeroLink} target='_blank'>
                  <span>Imported from Xero</span>{' '}
                  <span className='icon'>
                    <i className='fa fa-external-link'></i>
                  </span>
                </a>
              </h5>
            )
          }>
          <ModalLink modal='history' params={{itemType: 'Credit', itemId: creditId}} className='button is-white'>
            <span className='icon is-small'>
              <i className='fa fa-history' aria-hidden='true'></i>
            </span>
            <span>View History</span>
          </ModalLink>
          <a
            href={`/orgs/${organisationID}/downloads/credit.pdf?${toParams({credit_id: creditId})}`}
            target='_blank'
            className='button is-outlined is-dark'>
            Download PDF
          </a>
          {locked ? null : (
            <Fragment>
              <button className='button is-danger is-outlined' onClick={toggleConfirmDelete}>
                Delete
              </button>
              <button className='button is-primary' onClick={handleUpdate}>
                Save
              </button>
            </Fragment>
          )}
        </Header>
        <section className='section is-narrow'>
          <div className='container'>
            <form>
              <div className='field is-grouped'>
                <div className='control is-expanded'>
                  <DateField
                    name='date'
                    value={form.date || ''}
                    onChange={handleChange}
                    errors={errors.date}
                    disabled={locked || imported}
                    required
                  />
                </div>

                <div className='control is-expanded'>
                  <InputField
                    type='number'
                    name='amount'
                    value={form.amount || ''}
                    onChange={handleChange}
                    errors={errors.amount}
                    min={0}
                    autoFocus={true}
                    disabled={locked || imported}
                    required
                  />
                </div>
              </div>

              <div className='field'>
                <div className='control'>
                  <InputField
                    type='text'
                    name='description'
                    value={form.description || ''}
                    onChange={handleChange}
                    errors={errors.description}
                    disabled={locked}
                    required
                  />
                </div>
              </div>
            </form>
          </div>
        </section>
        {!!credit.invoice ? (
          <section className='py-3 section has-background-info'>
            <div className='container is-size-7 has-text-light'>
              <ShowField
                value={credit.invoice?.invoiceNumber}
                href={`/orgs/${organisationID}/invoices/${credit.invoice?.id}`}
                alwaysHorizontal
                rightAligned
                className='mb-0'
                labelClassName='has-text-light'
                label='Attached to'
              />
            </div>
          </section>
        ) : null}
      </Modal>

      {showConfirmDelete && (
        <Deletion
          confirm='Are you sure you want to delete?'
          onConfirm={() => destroy({}, history.goBack)}
          onClose={toggleConfirmDelete}
        />
      )}
    </Fragment>
  )
}
